import { Box, IconButton, Svg, Typography } from '@altermaind/web/ui';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { CardsContentSkeleton } from './CardsContentSkeleton';

type Props = {
  title?: string;
  category?: string;
  description: string;
  button: {
    iconName: (typeof Svg)['name'];
    text: string;
    loading?: boolean;
    onClick?: () => void;
  };
  mainInfoContent?: {
    text: string;
    iconName: (typeof Svg)['name'];
  };
  progressBar?: {
    label?: string;
    current: number;
    total: number;
  };
  loading?: boolean;
  halfHeightLayout?: boolean;
};

export const CardsContent = ({
  title,
  category,
  description,
  button,
  loading,
  mainInfoContent,
  progressBar,
  halfHeightLayout,
}: Props) => {
  const { genericResponsiveValue } = useResponsiveStyle();

  if (loading) {
    return <CardsContentSkeleton />;
  }

  return (
    <Box p='xs' flex={halfHeightLayout ? 2 : 1} display='flex' flexDirection='column'>
      {category ? (
        <Typography variant='p3/light' color='grey/light'>
          {category}
        </Typography>
      ) : null}
      {title ? <Typography variant='h4'>{title}</Typography> : null}
      {!halfHeightLayout ? (
        <Typography
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
          variant='p3/light'
          mt='xxs'
          color='grey/light'
          ellipsis
          numberOfLines={3}
        >
          {description}
        </Typography>
      ) : null}
      {progressBar && progressBar.total > 0 ? (
        <Box width='100%' mb='xs' mt='m'>
          <Box borderRadius='xxl' height='xxs' width='100%' backgroundColor='grey/dark/background'>
            <Box
              borderRadius='xxl'
              height='xxs'
              width={`${Math.round((progressBar.current / progressBar.total) * 100)}%`}
              backgroundColor='primary'
            />
          </Box>
          {progressBar.label ? (
            <Typography mt='xxs' variant='p3/light' color='grey/light'>
              {progressBar.label}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Box
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column-reverse',
        })}
        justifyContent={halfHeightLayout ? 'space-between' : 'flex-start'}
        alignItems={genericResponsiveValue({
          breakPoints: { desktop: 'flex-end' },
          defaultValue: 'flex-start',
        })}
        flex={halfHeightLayout ? 0 : 1}
        mt={halfHeightLayout ? 'auto' : 'm'}
      >
        <IconButton
          variant='primary'
          width='fit-content'
          prefixSvg={button.iconName}
          svgStyle={{
            size: 'xs',
          }}
          textStyle={{
            ml: 'xxxs',
          }}
          onClick={() => button?.onClick?.()}
          loading={button?.loading}
          disabled={button?.loading}
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xs',
          })}
        >
          {button.text}
        </IconButton>
        {/* MAIN INFO CONTENT */}
        {halfHeightLayout && mainInfoContent ? (
          <Box display='flex' flexWrap='wrap' justifyContent='flex-start' my='auto'>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Svg name={mainInfoContent.iconName} fill='primary/text' size='xs' />
              <Typography variant='p2' ml='xxs' color='primary/text'>
                {mainInfoContent.text}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
